import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdInsertLink, MdOutlineSearch } from 'react-icons/md';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useDebounce } from 'use-debounce';
import { useListAssociations } from 'legacy/groups/hooks';
import ItemList, { Item } from '../ItemList';
import EmptyItemSelectionList from '../EmptyItemSelectionList';

const AssociationSelectionModal = ({
  associations,
  onClose,
  onSubmit
}: {
  associations: Item[];
  onClose: () => void;
  onSubmit: (associations: Item[]) => void;
}) => {
  const { t } = useTranslation(['events']);
  const [selectedAssociations, setSelectedAssociations] =
    useState<Item[]>(associations);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 200);
  const ref = useRef<IntersectionObserver>();

  const {
    data: searchedAssociations,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useListAssociations(debouncedSearch);

  const handleSubmit = () => {
    if (search.length > 0) {
      setSearch('');
    } else {
      onSubmit(selectedAssociations);
    }
  };

  const eligibleAssociationsCount =
    selectedAssociations.length > 0 ? `• ${selectedAssociations.length}` : '';
  const footerText =
    search.length > 0
      ? t('settings.promoterOffers.selectionModals.associations.saveSelection')
      : `${t('settings.promoterOffers.selectionModals.associations.addEligible')} ${eligibleAssociationsCount}`;

  const handleSearch = ({ currentTarget }) => {
    setSearch(currentTarget.value);
  };

  const showEmptyState =
    !isLoading && !debouncedSearch && !selectedAssociations?.length;

  const handleSelectAssociation = (association: Item) => {
    const newSelectedAssociations = selectedAssociations.some(
      (a) => a.id === association.id
    )
      ? selectedAssociations.filter((a) => a.id !== association.id)
      : [...selectedAssociations, association];
    setSelectedAssociations(newSelectedAssociations);
  };

  const handleClearAll = () => {
    setSelectedAssociations([]);
  };

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading || isFetchingNextPage) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isLoading, hasNextPage]
  );

  const associationsToDisplay =
    search.length > 0
      ? searchedAssociations.map((association) => ({
          id: association.id,
          name: association.name,
          photoUrl: association?.logoImage || ''
        }))
      : selectedAssociations;

  return (
    <Modal
      visible
      autoHeight
      type={ModalType.SMALL}
      onClose={onClose}
      shouldStopModalClose={false}
      title={t('settings.promoterOffers.selectionModals.associations.title')}
      icon={<MdInsertLink size={20} />}
      renderFooter={() => (
        <Button
          type={ButtonType.Primary}
          onClick={handleSubmit}
          loading={false}
        >
          {footerText}
        </Button>
      )}
    >
      <div className="px-4 pt-4 flex flex-col justify-between">
        <p className="mt-4 text-base font-normal">
          {t('settings.promoterOffers.selectionModals.associations.description')}
        </p>
        <div className="relative shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-6">
          <MdOutlineSearch
            className="absolute left-3 text-gray-400 self-center justify-center"
            size={24}
          />
          <input
            type="text"
            className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
            placeholder={t('settings.promoterOffers.selectionModals.associations.searchPlaceholder')}
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div>
          {showEmptyState ? (
            <EmptyItemSelectionList message={t('settings.promoterOffers.selectionModals.associations.emptyState')} />
          ) : (
            <div className="overflow-y-auto mt-4">
              <ItemList
                isLoading={isLoading}
                items={associationsToDisplay}
                onSelect={handleSelectAssociation}
                selectedItems={selectedAssociations}
                search={search}
                onClearAll={handleClearAll}
                lastElementRef={lastElementRef}
                clearButtonText={`${selectedAssociations.length} ${t('settings.promoterOffers.selectionModals.associations.clearButton')}`}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AssociationSelectionModal;
